import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../hooks/useDebounce";
import useI18n from "../../../../../hooks/useTranslations";
import { AdminPatrolSystem } from "../../../../../http/patrol/types/patrolSystems";
import { AdminSupportExtension } from "../../../../../http/patrol/types/supportExtensions";
import { usePatrolSystemsApi } from "../../../../../http/patrol/usePatrolSystems";
import { useSupportExtensionsApi } from "../../../../../http/patrol/useSupportExtensions";
import { AdminItem, SearchAdminItem } from "../../../../../http/types/items";
import { useItemsApi } from "../../../../../http/useItems";
import Button from "../../../../ui/Button";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import Numeric from "../../../../ui/Numeric";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import { CustomAsyncSelect, CustomSelect } from "../../../../ui/select/CustomSelect";

interface SupportExtensionFormBody {
  item: AdminItem | null;
  system: AdminPatrolSystem | null;
  height: number | null;
  visibility: VisibilityScopeValue;
}

interface SupportExtensionFormProps {
  defaultValues?: AdminSupportExtension;
  onClose: () => void;
}

const SupportExtensionForm: FC<SupportExtensionFormProps> = ({ defaultValues, onClose }: SupportExtensionFormProps) => {
  const i18n = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useContext(NotificationDispatch);
  const aborter = useRef(new AbortController());
  const { patrolSystemId } = useParams<{ patrolSystemId: string }>();
  const [defaultOptions, setDefaultOptions] = useState<SearchAdminItem[]>([]);

  const supportExtensionsApi = useSupportExtensionsApi();
  const patrolSystemsApi = usePatrolSystemsApi();
  const itemsApi = useItemsApi();

  const patrolSystemsResult = patrolSystemsApi.useAdminPatrolSystems({ limit: 100 }, aborter.current.signal);

  return (
    <Form<SupportExtensionFormBody>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting}
      secondaryButton={
        <Button buttonProps={{ onClick: () => onClose() }} type="secondary">
          {i18n.translation.common.cancel}
        </Button>
      }
      onSubmit={() => (formData) =>
        defaultValues?.item.id
          ? supportExtensionsApi
              .updateAdminSupportExtension(defaultValues.item.id, {
                height: formData.height!,
                systemId: patrolSystemId ?? formData.system?.id!,
                visibility: formData.visibility,
              })
              .then(() => onClose())
              .catch((err) => dispatch(showErrorNotification(i18n, err)))
              .finally(() => setIsSubmitting(false))
          : supportExtensionsApi
              .insertAdminSupportExtension({
                itemId: formData.item?.id!,
                height: formData.height!,
                systemId: patrolSystemId ?? formData.system?.id!,
                visibility: formData.visibility,
              })
              .then(() => onClose())
              .catch((err) => dispatch(showErrorNotification(i18n, err)))
              .finally(() => setIsSubmitting(false))
      }
    >
      {({ control, setValue }) => {
        return (
          <FormFieldsContainer fullWidth>
            <Controller
              name="item"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => {
                return (
                  <>
                    <FormField label={i18n.translation.items.singular} error={fieldState.error}>
                      {({ labelId }) => {
                        const searchTerm = useRef("");
                        const aborter = useRef(new AbortController());

                        const loadOptions = createLoadOptions(aborter, searchTerm, itemsApi.getAdminItems);

                        // Fetch initial data on mount
                        useEffect(() => {
                          loadOptions(searchTerm.current).then(setDefaultOptions);
                        }, []);

                        return (
                          <CustomAsyncSelect
                            {...field}
                            inputId={labelId}
                            defaultOptions={defaultOptions}
                            loadOptions={loadOptions}
                            isClearable
                            getOptionLabel={(x) => x.code}
                            getOptionValue={(x) => x.id}
                            onChange={(value) => setValue("item", value ?? null)}
                          />
                        );
                      }}
                    </FormField>
                  </>
                );
              }}
            />
            <Controller
              name="system"
              control={control}
              rules={{ required: !patrolSystemId }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.patrolSystems.singular}
                  error={fieldState.error}
                  hidden={!!patrolSystemId}
                >
                  {({ labelId }) => (
                    <CustomSelect<AdminPatrolSystem>
                      {...field}
                      inputId={labelId}
                      options={patrolSystemsResult.data?.items ?? []}
                      isDisabled={!!patrolSystemsResult.error || isSubmitting}
                      isLoading={patrolSystemsResult.isLoading}
                      getOptionValue={(x) => x.id}
                      getOptionLabel={(x) => x.name}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="height"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.height} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("height", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope {...field} inputId={labelId} onChange={(value) => setValue("visibility", value)} />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default SupportExtensionForm;
